import { Inject, Injectable } from '@angular/core'
import jwt_decode from 'jwt-decode'
import { APP_CONFIG } from 'libs/shared/app-config/src/lib/gb-app-config'
import { isEqual } from 'lodash'
import { CookieService } from 'ngx-cookie-service'
import { BehaviorSubject, distinctUntilChanged } from 'rxjs'

export interface DecodedToken {
  v?: string // volunteer
  p?: string // guest
  u?: string // user
  a?: string // auction
  st?: boolean // staff
  su?: boolean // superuser
  iat: number
  exp: number
}

@Injectable({
  providedIn: 'root',
})
export class GbCookieService {
  private tokenCookieSubject$ = new BehaviorSubject<string | undefined>(this.getTokenCookie())
  tokenCookie$ = this.tokenCookieSubject$.asObservable().pipe(distinctUntilChanged((prev, curr) => isEqual(prev, curr)))

  get accessTokenCookieName(): string {
    return this.appConfig.accessTokenCookieName
  }

  constructor(protected cookieService: CookieService, @Inject(APP_CONFIG) protected appConfig: any) {}

  setCookie(name: string, value: string, expires?: number | Date): void {
    this.cookieService.set(name, value, {
      expires,
      path: '/',
      secure: true,
      sameSite: 'Strict',
    })
  }

  getCookie(name: string): string {
    return this.cookieService.get(name)
  }

  hasCookie(name: string): boolean {
    return this.cookieService.check(name)
  }

  deleteCookie(name: string, path = '/'): void {
    this.cookieService.delete(name, path)
  }

  setTokenCookie(token?: string): void {
    if (token) {
      const now = new Date()
      const expires = new Date(now.setMonth(now.getMonth() + 1))
      const value = { $$_: token }
      this.setCookie(this.accessTokenCookieName, JSON.stringify(value), expires)
    }

    this.tokenCookieSubject$.next(this.getTokenCookie())
  }

  getTokenCookie(): string | undefined {
    let token = this.getCookie(this.accessTokenCookieName)

    try {
      token = JSON.parse(token)
    } catch {
      return undefined
    }

    return token && Object.keys(token).length ? Object.values(token)[0] : undefined
  }

  getDecodedTokenCookie(token?: string): DecodedToken | undefined {
    if (!token) {
      token = this.getTokenCookie()
    }
    return token ? jwt_decode(token) : undefined
  }

  isAuthenticated(): boolean {
    return this.hasCookie(this.accessTokenCookieName)
  }

  isAdminOrVolunteer(token?: string): boolean {
    const decodedToken = this.getDecodedTokenCookie(token)
    return decodedToken ? !decodedToken.p : false
  }

  clearTokenCookie(): void {
    this.deleteCookie(this.accessTokenCookieName)
    this.tokenCookieSubject$.next(undefined)
  }
}
